import { useState } from 'react'
import {
   CardElement,
   Elements,
   useElements,
   useStripe,
} from '@stripe/react-stripe-js'
import { loadStripe } from '@stripe/stripe-js'
import { ENV } from '@forwardflow/helpers/configs'
import { OnboardingV2Service } from '@forwardflow/services/onboardingV2.service'
import { useOnboardingStoreV2 } from '@forwardflow/store/onboardingStoreV2'
import { isPaymentPricingReady } from '@forwardflow/modules/onboarding/payment-pricing'
import { isOperatorEmail } from '@forwardflow/utils/app.utils'
import { Screen } from '../frame/SetupFrame'
import { useStepFooter } from '../../hooks/useStepFooter'
import {
   Button,
   Checkbox,
   ErrorLine,
   IconDot,
   Input,
   Row,
   RowCard,
} from '../ui/primitives'
import { Icon } from '../ui/Icon'
import { pricingOf, startingBalanceFor } from '../../utils/budget'
import { seatCostFor } from '../../utils/seats'
import { errorText, plural } from '../../utils/format'

// Without a publishable key Stripe.js throws on load; a null promise keeps
// the form rendering (card box empty) so the step still works locally.
const stripePromise = ENV.STRIPE_ID ? loadStripe(ENV.STRIPE_ID) : null

const CARD_STYLE = {
   style: {
      base: {
         fontSize: '15px',
         color: '#ffffff',
         fontFamily:
            'Inter, system-ui, -apple-system, "Segoe UI", Roboto, sans-serif',
         '::placeholder': { color: '#6b7280' },
      },
      invalid: { color: '#f87171', iconColor: '#f87171' },
   },
   hidePostalCode: true,
}

/**
 * Step 6: the card.
 *
 * What is charged today (the seats plus a starting balance for calls) is in
 * the button. The card goes straight to Stripe; the backend subscribes the
 * organization to the tier with the seat count and the budgets from the two
 * steps before. The three consents are the ones the existing payment step
 * collects. Operators skip the charge, as before.
 */
const PaymentForm = () => {
   const { onboardingData, getNextStep, goToStep, setIsSubscriptionActivated } =
      useOnboardingStoreV2()
   const { isSubscriptionActivated, pricingData, email } = onboardingData
   const pricing = pricingOf(pricingData)
   const ready = isPaymentPricingReady(pricingData)
   const operator = isOperatorEmail(email || '')
   const seats = pricing.seatsCount
   const seatCost = seatCostFor(seats)
   const balance = startingBalanceFor(pricing.maxSpend)
   const today = seatCost + balance

   const stripe = useStripe()
   const elements = useElements()
   const [values, setValues] = useState({
      name: '',
      city: '',
      zip: '',
      coupon: '',
   })
   const [cardComplete, setCardComplete] = useState(false)
   const [terms, setTerms] = useState(false)
   const [policy, setPolicy] = useState(false)
   const [tokens, setTokens] = useState(false)
   const [paying, setPaying] = useState(false)
   const [error, setError] = useState<string | null>(null)

   const consents = terms && policy && tokens
   const formOk =
      cardComplete &&
      values.name.trim().length > 1 &&
      values.city.trim().length > 0 &&
      values.zip.length === 5 &&
      consents

   const pay = async () => {
      if (isSubscriptionActivated || operator) {
         getNextStep()
         return
      }
      if (!ready || !formOk) return
      if (!stripe || !elements) {
         setError('The card form is still loading. Try again in a moment.')
         return
      }
      setPaying(true)
      setError(null)
      try {
         const { error: stripeError, paymentMethod } =
            await stripe.createPaymentMethod({
               elements,
               params: {
                  billing_details: {
                     name: values.name,
                     address: { city: values.city, postal_code: values.zip },
                  },
               },
            })
         if (stripeError || !paymentMethod) {
            setError(stripeError?.message || 'Your card was not accepted.')
            return
         }
         const response = await OnboardingV2Service.subscribeToPackage({
            paymentInfo: {
               cardId:
                  ENV.NODE_ENV === 'development' || ENV.NODE_ENV === 'staging'
                     ? 'pm_card_visa'
                     : paymentMethod.id,
               nameOnCard: values.name,
               city: values.city,
               postalCode: values.zip,
               country: 'USA',
               addressLineOne: '123 Main St',
            },
            billingPeriod: 'monthly',
            tierId: pricing.selectedTierId as number,
            seatsCount: pricing.seatsCount,
            couponCode: values.coupon.trim(),
            newLeadBudget: pricing.newLeadBudget,
            reactivateLeadBudget: pricing.reactivateLeadBudget,
         })
         if (response?.success) {
            setIsSubscriptionActivated(true)
            getNextStep()
         } else {
            setError(
               errorText(
                  response,
                  'The payment did not go through. Check the card and try again.'
               )
            )
         }
      } catch (e) {
         setError(
            errorText(
               e,
               'The payment did not go through. Check the card and try again.'
            )
         )
      } finally {
         setPaying(false)
      }
   }

   const label = isSubscriptionActivated
      ? 'Continue'
      : operator
        ? 'Continue without a card (operator)'
        : !ready
          ? 'Confirm your team and budget first'
          : formOk
            ? `Pay $${today} and continue`
            : !cardComplete
              ? 'Fill in your card to continue'
              : !consents
                ? 'Accept the terms to continue'
                : 'Fill in your details to continue'

   useStepFooter(label, () => void pay(), {
      disabled: !isSubscriptionActivated && !operator && (!ready || !formOk),
      loading: paying,
      hint: isSubscriptionActivated
         ? 'Paid. Your receipt is in your inbox.'
         : `Then $${seatCost}/mo for ${plural(seats, 'seat')}, plus whatever tops the balance back up to $${pricing.maxSpend}.`,
      testId: 'pay',
   })

   if (isSubscriptionActivated)
      return (
         <RowCard testId="paid-card">
            <Row>
               <IconDot name="check" tone="green" />
               <div className="flex-1 min-w-0">
                  <p className="text-[15px] font-semibold">
                     Your subscription is active
                  </p>
                  <p className="text-[13px] text-[var(--ob-text-2)]">
                     Payment is set up. Continue to confirm the rest of your
                     setup.
                  </p>
               </div>
            </Row>
         </RowCard>
      )

   if (!ready)
      return (
         <RowCard testId="pricing-missing">
            <Row>
               <IconDot name="alert" tone="warn" />
               <div className="flex-1 min-w-0">
                  <p className="text-[15px] font-semibold">
                     Confirm your team and budget first
                  </p>
                  <p className="text-[13px] text-[var(--ob-text-2)]">
                     Your saved seats or budget are incomplete. Nothing has been
                     charged.
                  </p>
               </div>
               <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => goToStep('your-team')}
                  testId="review-seats"
               >
                  Review
               </Button>
            </Row>
         </RowCard>
      )

   return (
      <div className="space-y-3">
         <label className="block">
            <span className="block text-[13px] font-medium text-[var(--ob-text-2)] mb-1.5">
               Card
            </span>
            <div
               className="rounded-2xl border border-[var(--ob-border)] bg-[var(--ob-surface)] px-4 py-3.5 hover:border-[var(--ob-border-strong)] focus-within:border-[var(--ob-primary)] focus-within:ring-4 focus-within:ring-[var(--ob-primary-tint)] transition-colors"
               data-testid="card-element"
            >
               <CardElement
                  options={CARD_STYLE}
                  onChange={(e) => setCardComplete(e.complete)}
               />
            </div>
         </label>
         <div className="grid grid-cols-2 gap-3">
            <Input
               label="Name on card"
               value={values.name}
               onChange={(v) => setValues({ ...values, name: v })}
               placeholder="Sam Patel"
               testId="card-name"
            />
            <Input
               label="Billing ZIP"
               value={values.zip}
               onChange={(v) =>
                  setValues({
                     ...values,
                     zip: v.replace(/\D/g, '').slice(0, 5),
                  })
               }
               placeholder="80903"
               testId="card-zip"
               inputMode="numeric"
            />
         </div>
         <div className="grid grid-cols-2 gap-3">
            <Input
               label="City"
               value={values.city}
               onChange={(v) => setValues({ ...values, city: v })}
               placeholder="Colorado Springs"
               testId="card-city"
            />
            <Input
               label="Coupon code"
               value={values.coupon}
               onChange={(v) => setValues({ ...values, coupon: v })}
               placeholder="Optional"
               testId="card-coupon"
            />
         </div>
         <div
            className="rounded-2xl border border-[var(--ob-border)] bg-[var(--ob-surface)] px-4 py-3 text-[13px]"
            data-testid="charge-summary"
         >
            <div className="flex items-center justify-between">
               <span className="text-[var(--ob-text-2)]">
                  {plural(seats, 'seat')}
               </span>
               <span>${seatCost}</span>
            </div>
            <div className="flex items-center justify-between mt-1">
               <span className="text-[var(--ob-text-2)]">
                  Starting balance for calls
               </span>
               <span>${balance}</span>
            </div>
            <div className="flex items-center justify-between mt-2 pt-2 border-t border-[var(--ob-border)] font-semibold">
               <span>Charged today</span>
               <span data-testid="charge-total">${today}</span>
            </div>
         </div>
         <div className="space-y-2 pt-1">
            <Checkbox
               on={terms}
               onChange={setTerms}
               testId="consent-terms"
               label={
                  <>
                     I agree to the{' '}
                     <a
                        className="text-[var(--ob-light)] hover:underline"
                        href="https://forward-flow.com/terms"
                        target="_blank"
                        rel="noreferrer"
                     >
                        Terms of Service
                     </a>
                  </>
               }
            />
            <Checkbox
               on={policy}
               onChange={setPolicy}
               testId="consent-policy"
               label={
                  <>
                     I agree to the{' '}
                     <a
                        className="text-[var(--ob-light)] hover:underline"
                        href="https://forward-flow.com/privacy"
                        target="_blank"
                        rel="noreferrer"
                     >
                        Privacy Policy
                     </a>
                  </>
               }
            />
            <Checkbox
               on={tokens}
               onChange={setTokens}
               testId="consent-tokens"
               label={
                  <>
                     I agree to the{' '}
                     <a
                        className="text-[var(--ob-light)] hover:underline"
                        href="https://forward-flow.com/token-usage-policy"
                        target="_blank"
                        rel="noreferrer"
                     >
                        Token Usage Policy
                     </a>
                  </>
               }
            />
         </div>
         {error && <ErrorLine testId="pay-error">{error}</ErrorLine>}
         <p className="text-center text-[12px] text-[var(--ob-text-3)] flex items-center justify-center gap-1.5 pt-1">
            <Icon name="lock" size={12} /> Card details go straight to Stripe.
            We never see them.
         </p>
      </div>
   )
}

export const PaymentStep = () => {
   const { onboardingData } = useOnboardingStoreV2()
   const pricing = pricingOf(onboardingData.pricingData)
   const seats = pricing.seatsCount
   const today = seatCostFor(seats) + startingBalanceFor(pricing.maxSpend)
   return (
      <Screen
         stepKey="payment"
         kind="payment"
         title={
            onboardingData.isSubscriptionActivated
               ? 'Your card is on file'
               : 'Add your card'
         }
         lede={
            onboardingData.isSubscriptionActivated ? (
               'Payment is set up.'
            ) : (
               <span>
                  Charged today: {plural(seats, 'seat')} (${seatCostFor(seats)})
                  plus a ${startingBalanceFor(pricing.maxSpend)} starting
                  balance for calls, ${today} in total.
               </span>
            )
         }
         width="sm"
      >
         <Elements stripe={stripePromise}>
            <PaymentForm />
         </Elements>
      </Screen>
   )
}
